

export abstract class DateHelper {
	/*
	DATE FORMATTING PARAMETERS - based on C#
	d		Represents the day of the month as a number from 1 through 31
	dd		Represents the day of the month as a number from 01 through 31
	ddd		Represents the abbreviated name of the day (Mon, Tue, Wed, etc)
	dddd	Represents the full name of the day (Monday, Tuesday, etc)
	h		12-hour clock hour (e.g. 4)
	hh		12-hour clock, with a leading 0 (e.g. 06)
	H		24-hour clock hour (e.g. 15)
	HH		24-hour clock hour, with a leading 0 (e.g. 22)
	mm		Minutes with a leading zero
	M		Month number (e.g. 3)
	MM		Month number with leading zero (e.g. 04)
	MMM		Abbreviated Month Name (e.g. Dec)
	MMMM	Full month name (e.g. December)
	s		Seconds
	ss		Seconds with leading zero
	tt		AM / PM
	y		Year, no leading zero (e.g. 2015 would be 15)
	yyyy	Year, (e.g. 2015)
	z		UTC offset (e.g. "UTC-6")
	Z		Timezone abbreviation (e.g. "MDT")
	*/

	public static standardizeDate(input: Date | string | null): Date | null {
		if (input === null) {
			return null;
		}

		if (input instanceof Date) {
			return input;
		}

		if (typeof input === 'string') {
			const parsedDate = new Date(input);
			return isNaN(parsedDate.getTime()) ? null : parsedDate;
		}

		return null;
	}

	/**
	 * Formats a date according to the provided format string, because Javascript has not seen fit to provide a native method for this elementary task.
	 * @param date A Date object or string.
	 * @param format A string representing the desired format. Uses C# formatting parameters. See this class for full list.
	 * @returns empty string if date is null or unparseable, otherwise the formatted date string for UTC time.
	 */
	public static formatDateUtc(input: Date | string | null, format: string): string {
		const date = this.standardizeDate(input);
		if (date == null) return "";

		const parsed = {
			seconds: date.getUTCSeconds(),
			minutes: date.getUTCMinutes(),
			hours: date.getUTCHours(),
			hours12: date.getUTCHours() == 0 ? 12 : (date.getUTCHours() > 12 ? date.getUTCHours() - 12 : date.getUTCHours()),
			weekday: date.getUTCDay(),
			day: date.getUTCDate(),
			month: date.getUTCMonth() + 1,
			year: date.getUTCFullYear(),
			utcText: "UTC",
			timezoneName: this.getTimezoneName(date),
		} as IParsedDate;

		return this.formatDate(parsed, format);
	}

	public static formatDateLocal(input: Date | string | null, format: string): string {
		const date = this.standardizeDate(input);
		if (date == null) return "";

		// Parsing the date parts
		const parsed = {
			seconds: date.getSeconds(),
			minutes: date.getMinutes(),
			hours: date.getHours(),
			hours12: date.getHours() === 0 ? 12 : (date.getHours() > 12 ? date.getHours() - 12 : date.getHours()),
			weekday: date.getDay(),
			day: date.getDate(),
			month: date.getMonth() + 1,
			year: date.getFullYear(),
			milliseconds: date.getMilliseconds(),
			utcText: "UTC-" + (date.getTimezoneOffset() / 60),
			timezoneName: this.getTimezoneName(date),
		} as IParsedDate;

		return this.formatDate(parsed, format);
	}

	/**
	 * Formats a date to be used in a date entry field.
	 * @param input The date to be edited
	 */
	public static formatForEdit(input: Date | string | null): string {
		return this.formatDateUtc(input, "yyyy-MM-dd");
	}

	private static getTimezoneName(date: Date): string {
		return Intl.DateTimeFormat().resolvedOptions().timeZone;
	}

	private static formatDate(parsed: IParsedDate, format: string): string {
		// Handling the custom format string
		return format
			.replace('yyyy', String(parsed.year))
			.replace('MM', String(parsed.month).padStart(2, '0'))
			.replace('dd', String(parsed.day).padStart(2, '0'))
			.replace('HH', String(parsed.hours).padStart(2, '0'))
			.replace('mm', String(parsed.minutes).padStart(2, '0'))
			.replace('ss', String(parsed.seconds).padStart(2, '0'))
			.replace('SSS', String(parsed.milliseconds).padStart(3, '0'));
	}

	public static getAge(birthDate: Date): number {
		const now = new Date();
		const birth = new Date(birthDate);

		let age = now.getFullYear() - birth.getFullYear();

		// Adjust if the birth date hasn't occurred yet this year
		const monthDiff = now.getMonth() - birth.getMonth();
		const dayDiff = now.getDate() - birth.getDate();

		if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
			age--;
		}

		return age;
	}

	private static getMonthName(monthNum: number): string {
		switch (monthNum) {
			case 1: return "January";
			case 2: return "February";
			case 3: return "March";
			case 4: return "April";
			case 5: return "May";
			case 6: return "June";
			case 7: return "July";
			case 8: return "August";
			case 9: return "September";
			case 10: return "October";
			case 11: return "November";
			case 12: return "December";
			default: throw new Error("Invalid month number");
		}
	}

	private static getMonthAbbreviation(monthNum: number): string {
		switch (monthNum) {
			case 1: return "Jan";
			case 2: return "Feb";
			case 3: return "Mar";
			case 4: return "Apr";
			case 5: return "May";
			case 6: return "Jun";
			case 7: return "Jul";
			case 8: return "Aug";
			case 9: return "Sep";
			case 10: return "Oct";
			case 11: return "Nov";
			case 12: return "Dec";
			default: throw new Error("Invalid month number");
		}
	}

	private static getDayName(dayNum: number): string {
		switch (dayNum) {
			case 0: return "Sunday";
			case 1: return "Monday";
			case 2: return "Tuesday";
			case 3: return "Wednesday";
			case 4: return "Thursday";
			case 5: return "Friday";
			case 6: return "Saturday";
			default: throw new Error("Invalid day number");
		}
	}

	private static getDayAbbreviation(dayNum: number): string {
		switch (dayNum) {
			case 0: return "Sun";
			case 1: return "Mon";
			case 2: return "Tue";
			case 3: return "Wed";
			case 4: return "Thu";
			case 5: return "Fri";
			case 6: return "Sat";
			default: throw new Error("Invalid day number");
		}
	}

	//private static getTimezoneName(date: Date | null): string {
	//	if (date == null) {
	//		return "";
	//	}
	//	else {
	//		try {
	//			const dateSplit = date.toLocaleDateString(undefined, { timeZoneName: 'short' }).split(" ");
	//			return dateSplit[dateSplit.length - 1];
	//		}
	//		catch (e: any) {
	//			return "";
	//		}
	//	}
	//}
}


interface IParsedDate {
	seconds: number,
	minutes: number,
	hours: number,
	hours12: number,
	weekday: number,
	day: number,
	month: number,
	year: number,
	milliseconds: number;
	utcText: string,
	timezoneName: string,
}
